import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AIChatbotSettings } from '@/types/ai';
import { chatLogger } from '@/utils/logger';
import { measureAsync } from '@/utils/performance';

/**
 * Hook for managing chatbot settings
 * Handles loading and caching of bot configuration
 */

export interface ChatbotSettingsManager {
  // State
  botSettings: AIChatbotSettings;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadSettings: (chatbotId: string) => Promise<void>;
  updateSettings: (settings: Partial<AIChatbotSettings>) => void;
  resetToDefaults: () => void;
}

const DEFAULT_SETTINGS: AIChatbotSettings = {
  messageDelay: 1000,
  showTypingIndicator: true,
  messageStyle: 'streaming',
  typingSpeed: 30,
  streamingForAIOnly: false,
  aiServices: {
    primary: {
      provider: 'openai',
      model: 'gpt-4',
      temperature: 0.7,
      max_tokens: 2048
    }
  }
};

export function useChatbotSettings(): ChatbotSettingsManager {
  const [botSettings, setBotSettings] = useState<AIChatbotSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = useCallback(async (chatbotId: string) => {
    if (!chatbotId) {
      chatLogger.error('Cannot load settings without chatbotId');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const settings = await measureAsync(
        `load-chatbot-settings-${chatbotId}`,
        async () => {
          chatLogger.conversation('Loading chatbot settings', { chatbotId });

          const { data, error: fetchError } = await supabase
            .from('chatbots')
            .select('ai_settings, settings')
            .eq('id', chatbotId)
            .single();

          // Parse settings if it's a string
          let parsedSettings = data?.settings;
          if (typeof parsedSettings === 'string') {
            try {
              parsedSettings = JSON.parse(parsedSettings);
            } catch (e) {
              console.error('Error parsing settings:', e);
              parsedSettings = {};
            }
          }

          console.log('🔍 CHATBOT SETTINGS DEBUG - Database fetch:', {
            chatbotId,
            hasAiSettings: !!data?.ai_settings,
            hasSettings: !!data?.settings,
            settingsType: typeof data?.settings,
            aiSettingsKeys: data?.ai_settings ? Object.keys(data.ai_settings) : [],
            settingsKeys: parsedSettings ? Object.keys(parsedSettings) : [],
            settingsChatMode: parsedSettings?.chatMode,
            rawSettingsPreview: typeof data?.settings === 'string' ? data.settings.substring(0, 100) + '...' : 'not a string'
          });

          if (fetchError) {
            throw new Error(`Failed to fetch chatbot settings: ${fetchError.message}`);
          }

          // Check both ai_settings and settings fields
          let mergedSettings = { ...DEFAULT_SETTINGS };
          
          if (data?.ai_settings) {
            mergedSettings = { ...mergedSettings, ...data.ai_settings };
          }
          
          if (parsedSettings) {
            // Use parsedSettings (already parsed from string) instead of data.settings
            mergedSettings = { ...mergedSettings, ...parsedSettings };
          }

          console.log('🔍 CHATBOT SETTINGS DEBUG - Merged settings:', {
            chatMode: mergedSettings.chatMode,
            hasChatMode: 'chatMode' in mergedSettings,
            settingsKeys: Object.keys(mergedSettings)
          });

          return mergedSettings as AIChatbotSettings;
        },
        'network'
      );

      setBotSettings(settings);
      chatLogger.conversation('Chatbot settings loaded successfully', {
        chatbotId,
        settings: {
          messageDelay: settings.messageDelay,
          messageStyle: settings.messageStyle,
          provider: settings.aiServices?.primary?.provider
        }
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error loading settings';
      setError(errorMessage);
      chatLogger.error('Failed to load chatbot settings', {
        chatbotId,
        error: errorMessage
      });

      // Fall back to default settings
      setBotSettings(DEFAULT_SETTINGS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateSettings = useCallback((newSettings: Partial<AIChatbotSettings>) => {
    setBotSettings(prev => {
      const updated = { ...prev, ...newSettings };
      
      chatLogger.conversation('Chatbot settings updated', {
        changes: Object.keys(newSettings),
        newSettings: updated
      });
      
      return updated;
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    setBotSettings(DEFAULT_SETTINGS);
    setError(null);
    
    chatLogger.conversation('Chatbot settings reset to defaults');
  }, []);

  return {
    botSettings,
    isLoading,
    error,
    loadSettings,
    updateSettings,
    resetToDefaults
  };
}
